import React, { useState, useEffect, useCallback } from "react";
import {
  Clock, Calendar, CheckCircle2,
  Flame, Trophy, FileSpreadsheet, CheckCheck,
  MapPin, Sparkles, CheckSquare, Compass,
  AlertCircle, Loader2, CircleDot, Ban, CalendarCheck
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabaseClient";

const STATUS_CONFIG = {
  pending: { label: "Pending", icon: CircleDot, className: "bg-amber-50 text-amber-700 border-amber-200" },
  present: { label: "Present", icon: CheckCheck, className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  absent: { label: "Absent", icon: Ban, className: "bg-red-50 text-red-700 border-red-200" },
};

// Robust date parser supporting YYYY-MM-DD, ISO, and human formats like "Sep 12, 2026"
const isPastDate = (dateStr) => {
  if (!dateStr) return false;
  const parsed = new Date(dateStr);
  if (isNaN(parsed.getTime())) return false;
  const target = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return target < today;
};

const StudentDashboard = () => {
  const { user } = useAuth();

  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(true);

  const [availableEvents, setAvailableEvents] = useState([]);
  const [registeredEvents, setRegisteredEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [volunteerProfile, setVolunteerProfile] = useState(null);

  // 1. Fetch Volunteer Profile to get College Name
  const fetchVolunteerProfile = useCallback(async () => {
    if (!user?.volunteerId) return;
    const { data } = await supabase
      .from("volunteers")
      .select("id, name, email, college_name")
      .eq("id", user.volunteerId)
      .single();
    if (data) setVolunteerProfile(data);
  }, [user?.volunteerId]);

  // 2. Fetch Live Attendance Records
  const fetchAttendance = useCallback(async () => {
    if (!user?.volunteerId) {
      setAttendanceLoading(false);
      return;
    }
    const { data } = await supabase
      .from("attendance_log")
      .select("*")
      .eq("volunteer_id", user.volunteerId)
      .order("login_time", { ascending: false });

    setAttendanceRecords(data || []);
    setAttendanceLoading(false);
  }, [user?.volunteerId]);

  // 3. Fetch Events & Auto-Filter by Date and Attendance Status
  const fetchEventsData = useCallback(async () => {
    if (!user?.volunteerId) {
      setEventsLoading(false);
      return;
    }
    setEventsLoading(true);

    const [eventsRes, regRes] = await Promise.all([
      supabase.from("events").select("*").order("created_at", { ascending: false }),
      supabase
        .from("event_registrations")
        .select("id, event_id, status, events(*)")
        .eq("volunteer_id", user.volunteerId),
    ]);

    const userCollege = volunteerProfile?.college_name;
    const allRegisteredList = regRes.data || [];

    // Set of events where attendance has already been marked
    const markedEventNames = new Set(
      attendanceRecords
        .filter((att) => att.status === "present" || att.status === "absent")
        .map((att) => att.event_name)
    );

    // 1. Filter My Registered Events: Hide if date has passed or attendance was marked
    const activeRegistered = allRegisteredList.filter((reg) => {
      const evt = reg.events;
      if (!evt) return false;
      const isMarked = markedEventNames.has(evt.title);
      const isPast = isPastDate(evt.date);
      return !isMarked && !isPast;
    });

    setRegisteredEvents(activeRegistered);

    const registeredIds = new Set(allRegisteredList.map((r) => r.event_id));

    // 2. Filter Upcoming Events: Hide past dates and already-registered drives
    const filteredUpcoming = (eventsRes.data || []).filter((e) => {
      if (registeredIds.has(e.id)) return false;
      if (isPastDate(e.date)) return false;
      if (!e.college_name || e.college_name === "All Colleges") return true;
      return userCollege ? e.college_name === userCollege : true;
    });

    setAvailableEvents(filteredUpcoming);
    setEventsLoading(false);
  }, [user?.volunteerId, volunteerProfile?.college_name, attendanceRecords]);

  useEffect(() => {
    fetchVolunteerProfile();
  }, [fetchVolunteerProfile]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  useEffect(() => {
    fetchEventsData();

    if (!user?.volunteerId) return;

    // Realtime attendance updates
    const channelName = `student-dashboard-${user.volunteerId}-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "attendance_log",
          filter: `volunteer_id=eq.${user.volunteerId}`,
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            setAttendanceRecords((prev) => [payload.new, ...prev]);
          } else if (payload.eventType === "UPDATE") {
            setAttendanceRecords((prev) =>
              prev.map((r) => (r.id === payload.new.id ? payload.new : r))
            );
          } else if (payload.eventType === "DELETE") {
            setAttendanceRecords((prev) =>
              prev.filter((r) => r.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchAttendance, fetchEventsData, user?.volunteerId]);

  // Register for Event
  const handleRegister = async (event) => {
    if (!user?.volunteerId) return;
    setActionLoadingId(event.id);

    const { error } = await supabase.from("event_registrations").insert({
      event_id: event.id,
      volunteer_id: user.volunteerId,
      status: "Confirmed",
    });

    if (!error) {
      await fetchEventsData();
    }
    setActionLoadingId(null);
  };

  const formatDate = (val) =>
    val
      ? new Date(val).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  const totalHours = attendanceRecords.reduce(
    (sum, r) => sum + (r.status === "present" ? Number(r.hours || 4) : 0),
    0
  );
  const presentCount = attendanceRecords.filter((r) => r.status === "present").length;

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-gray-800 antialiased pb-16 w-full overflow-x-hidden">
      {/* Hero Header */}
      <section className="relative w-full py-8 md:py-10 bg-gradient-to-br from-[#4E1E82] via-[#3B1569] to-[#250B48] text-white overflow-hidden px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] font-black uppercase tracking-widest text-amber-300 mb-3">
            <Flame className="w-3.5 h-3.5 text-amber-400" />
            Kindness Champion
          </div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight">
                Welcome back, <span className="text-amber-300">{user?.name || "Volunteer"}</span> ✨
              </h1>
              <p className="text-white/80 text-xs sm:text-sm mt-1.5 font-medium">
                {volunteerProfile?.college_name ? `${volunteerProfile.college_name} • ` : ""}
                Explore upcoming events and monitor your live attendance status.
              </p>
            </div>
            <div className="rounded-xl border border-white/20 bg-white/10 backdrop-blur-md p-4 min-w-[200px] text-xs self-start md:self-auto">
              <div className="flex items-center justify-between font-bold">
                <span className="flex items-center gap-1 text-amber-300">
                  <Trophy className="w-3.5 h-3.5" /> Milestone Progress
                </span>
                <span>{totalHours} / 60 hrs</span>
              </div>
              <div className="h-2 bg-black/20 rounded-full mt-2.5 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 rounded-full transition-all duration-700"
                  style={{ width: `${Math.min((totalHours / 60) * 100, 100)}%` }}
                />
              </div>
              <p className="text-right text-[10px] text-white/70 mt-2 font-medium">
                {Math.max(60 - totalHours, 0)} hours to reach Gold Tier
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stat Cards */}
      <section className="relative -mt-6 max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5">
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-purple-600 flex items-center justify-center text-white">
                <Clock className="w-4 h-4" />
              </div>
              <span className="text-xs font-extrabold text-gray-400 uppercase">Hours Contributed</span>
            </div>
            <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
              Live
            </span>
          </div>
          <div className="mt-3 sm:mt-4 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-gray-900">{totalHours}</span>
            <span className="text-xs font-bold text-gray-400">HRS</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-violet-700 flex items-center justify-center text-white">
                <Calendar className="w-4 h-4" />
              </div>
              <span className="text-xs font-extrabold text-gray-400 uppercase">Active Drives</span>
            </div>
            <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
              {registeredEvents.length} Active
            </span>
          </div>
          <div className="mt-3 sm:mt-4 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-gray-900">{registeredEvents.length}</span>
            <span className="text-xs font-bold text-gray-400">REGISTERED</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-fuchsia-600 flex items-center justify-center text-white">
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <span className="text-xs font-extrabold text-gray-400 uppercase">Present Sessions</span>
            </div>
            <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200">
              {attendanceRecords.length ? `${Math.round((presentCount / attendanceRecords.length) * 100)}% Rate` : "0% Rate"}
            </span>
          </div>
          <div className="mt-3 sm:mt-4 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-gray-900">{presentCount}</span>
            <span className="text-xs font-bold text-gray-400">PRESENT</span>
          </div>
        </div>
      </section>

      {/* Main Grid: Attendance + Events */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 sm:pt-10 grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 items-start">
        {/* Left Column: Attendance Log */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-black text-[#3B1569] flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-purple-600" />
              My Attendance Records
            </h2>
            <span className="text-xs font-bold bg-purple-100 text-purple-800 px-2.5 py-0.5 rounded-full">
              {attendanceRecords.length} Records
            </span>
          </div>

          <div className="bg-white rounded-2xl border border-purple-100 shadow-sm overflow-hidden">
            {attendanceLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-7 h-7 text-ku-purple animate-spin" />
              </div>
            ) : attendanceRecords.length === 0 ? (
              <div className="text-center py-16 text-gray-400 px-4">
                <CalendarCheck className="w-10 h-10 mx-auto mb-2 text-purple-300" />
                <p className="font-bold">No attendance records yet</p>
                <p className="text-xs mt-1">Attendance marked by admin will appear here live.</p>
              </div>
            ) : (
              <>
                {/* Mobile View */}
                <div className="block md:hidden divide-y divide-gray-100">
                  {attendanceRecords.map((rec) => {
                    const cfg = STATUS_CONFIG[rec.status] || STATUS_CONFIG.pending;
                    const StatusIcon = cfg.icon;
                    return (
                      <div key={rec.id} className="p-4 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-bold text-gray-900 text-sm truncate">
                            {rec.event_name || "Volunteering Session"}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatDate(rec.attendance_date || rec.login_time)}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${cfg.className}`}
                          >
                            <StatusIcon className="w-3 h-3" /> {cfg.label}
                          </span>
                          <div className="text-[11px] font-bold text-gray-600 mt-1">
                            {rec.status === "present" ? `+${rec.hours || 4} hrs` : "0 hrs"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left text-xs min-w-[550px]">
                    <thead className="bg-[#FAF7FD] border-b border-purple-100 font-extrabold text-purple-900 uppercase">
                      <tr>
                        <th className="py-3.5 px-4">Event</th>
                        <th className="py-3.5 px-4">Date</th>
                        <th className="py-3.5 px-4 text-center">Status</th>
                        <th className="py-3.5 px-4 text-right">Hours</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {attendanceRecords.map((rec) => {
                        const cfg = STATUS_CONFIG[rec.status] || STATUS_CONFIG.pending;
                        const StatusIcon = cfg.icon;
                        return (
                          <tr key={rec.id} className="hover:bg-purple-50/20">
                            <td className="py-3.5 px-4 font-bold text-gray-900">
                              {rec.event_name || "Volunteering Session"}
                            </td>
                            <td className="py-3.5 px-4 text-gray-600">
                              {formatDate(rec.attendance_date || rec.login_time)}
                            </td>
                            <td className="py-3.5 px-4 text-center">
                              <span
                                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-bold border ${cfg.className}`}
                              >
                                <StatusIcon className="w-3 h-3" /> {cfg.label}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-right font-bold text-gray-700">
                              {rec.status === "present" ? `+${rec.hours || 4} hrs` : "0 hrs"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Column: College Events & Registrations */}
        <div className="space-y-6">
          {/* Upcoming Events */}
          <div>
            <h2 className="text-base sm:text-lg font-black text-[#3B1569] flex items-center gap-2 mb-3">
              <Compass className="w-5 h-5 text-purple-600" />
              Upcoming College Events
            </h2>

            <div className="space-y-3">
              {eventsLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-6 h-6 text-ku-purple animate-spin" />
                </div>
              ) : availableEvents.length > 0 ? (
                availableEvents.map((evt) => (
                  <div
                    key={evt.id}
                    className="p-4 rounded-2xl bg-white border border-purple-100 shadow-sm hover:shadow-md transition-all"
                  >
                    <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                      {evt.college_name || "Open for All"}
                    </span>
                    <h3 className="font-bold text-gray-900 text-sm mt-2">{evt.title}</h3>
                    <div className="mt-2 text-xs text-gray-500 space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                        <span>
                          {evt.date} • {evt.time}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                        <span className="truncate">{evt.location}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRegister(evt)}
                      disabled={actionLoadingId === evt.id}
                      className="mt-3 w-full py-2 rounded-xl bg-ku-purple text-white text-xs font-bold hover:bg-ku-violet transition-colors flex items-center justify-center gap-1.5 border-none cursor-pointer disabled:opacity-50"
                    >
                      {actionLoadingId === evt.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="w-3.5 h-3.5 text-amber-300" /> Register for Event
                        </>
                      )}
                    </button>
                  </div>
                ))
              ) : (
                <div className="p-4 sm:p-5 rounded-2xl bg-white border border-dashed border-purple-200 text-center text-xs text-gray-400 font-medium">
                  <AlertCircle className="w-5 h-5 mx-auto mb-1 text-purple-400" />
                  No new events scheduled for your college right now.
                </div>
              )}
            </div>
          </div>

          {/* Registered Events */}
          <div>
            <h2 className="text-base sm:text-lg font-black text-[#3B1569] flex items-center gap-2 mb-3">
              <CheckSquare className="w-5 h-5 text-purple-700" />
              My Registered Events
            </h2>

            <div className="space-y-3">
              {registeredEvents.length > 0 ? (
                registeredEvents.map((reg) => {
                  const evt = reg.events;
                  return (
                    <div
                      key={reg.id}
                      className="p-4 rounded-2xl bg-purple-50/40 border border-purple-200 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                          Confirmed
                        </span>
                        <span className="text-[11px] font-bold text-gray-500">{evt?.date}</span>
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm mt-2">{evt?.title || "Event"}</h3>
                      <div className="mt-2 text-xs text-gray-500 space-y-1">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                          <span>{evt?.time}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                          <span className="truncate">{evt?.location}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-4 rounded-2xl bg-white border border-dashed border-gray-200 text-center text-xs text-gray-400 font-medium">
                  No active registered events.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudentDashboard;